import { LiveData, Service } from '@toeverything/infra';

import type { FavoriteSupportTypeUnion } from '../constant';
import { FavoriteList } from '../entities/favorite-list';

export const FAVORITE_REMOVAL_UNDO_MS = 3000;

export interface PendingFavoriteRemoval {
  deadline: number;
  id: string;
  startedAt: number;
  type: FavoriteSupportTypeUnion;
}

function favoriteRemovalKey(type: FavoriteSupportTypeUnion, id: string) {
  return `${type}:${id}`;
}

export class FavoriteService extends Service {
  favoriteList = this.framework.createEntity(FavoriteList);

  readonly pendingRemovals$ = new LiveData<
    ReadonlyMap<string, PendingFavoriteRemoval>
  >(new Map());

  private readonly pendingRemovalTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();

  pendingRemoval$(type: FavoriteSupportTypeUnion, id: string) {
    const key = favoriteRemovalKey(type, id);
    return this.pendingRemovals$.map(removals => removals.get(key) ?? null);
  }

  togglePendingRemoval(
    type: FavoriteSupportTypeUnion,
    id: string,
    delayMs = FAVORITE_REMOVAL_UNDO_MS
  ) {
    const key = favoriteRemovalKey(type, id);
    if (this.pendingRemovals$.value.has(key)) {
      this.cancelPendingRemoval(type, id);
      return;
    }
    if (!this.isFavorite(type, id)) {
      return;
    }

    const startedAt = Date.now();
    const deadline = startedAt + delayMs;
    const pendingRemoval: PendingFavoriteRemoval = {
      deadline,
      id,
      startedAt,
      type,
    };
    const nextRemovals = new Map(this.pendingRemovals$.value);
    nextRemovals.set(key, pendingRemoval);
    this.pendingRemovals$.setValue(nextRemovals);

    const timer = setTimeout(
      () => {
        const current = this.pendingRemovals$.value.get(key);
        if (current?.deadline !== deadline) {
          return;
        }
        this.pendingRemovalTimers.delete(key);
        if (this.isFavorite(type, id)) {
          this.favoriteList.remove(type, id);
        }
        this.clearPendingRemoval(key);
      },
      Math.max(0, delayMs)
    );
    this.pendingRemovalTimers.set(key, timer);
  }

  cancelPendingRemoval(type: FavoriteSupportTypeUnion, id: string) {
    const key = favoriteRemovalKey(type, id);
    const timer = this.pendingRemovalTimers.get(key);
    if (timer) {
      clearTimeout(timer);
      this.pendingRemovalTimers.delete(key);
    }
    this.clearPendingRemoval(key);
  }

  private clearPendingRemoval(key: string) {
    if (!this.pendingRemovals$.value.has(key)) {
      return;
    }
    const nextRemovals = new Map(this.pendingRemovals$.value);
    nextRemovals.delete(key);
    this.pendingRemovals$.setValue(nextRemovals);
  }

  private isFavorite(type: FavoriteSupportTypeUnion, id: string) {
    return this.favoriteList.sortedList$.value.some(
      favorite => favorite.type === type && favorite.id === id
    );
  }

  override dispose(): void {
    for (const timer of this.pendingRemovalTimers.values()) {
      clearTimeout(timer);
    }
    this.pendingRemovalTimers.clear();
    this.pendingRemovals$.setValue(new Map());
    super.dispose();
  }
}
